import React, { useState, useEffect } from 'react';
import { Save, Clock, MapPin, Phone, Mail, Instagram, RefreshCw } from 'lucide-react';
import { getSettings, updateSettings } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'sonner';

const Settings = () => {
  const { refreshSettings } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    location: '',
    address: '',
    phone: '',
    email: '',
    whatsapp: '',
    instagram: '',
    instagram_url: '',
    hours_weekdays: '',
    hours_saturday: '',
    hours_sunday: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        setFormData({
          name: settings.name || '',
          tagline: settings.tagline || '',
          description: settings.description || '',
          location: settings.location || '',
          address: settings.address || '',
          phone: settings.phone || '',
          email: settings.email || '',
          whatsapp: settings.whatsapp || '',
          instagram: settings.instagram || '',
          instagram_url: settings.instagram_url || '',
          hours_weekdays: settings.hours_weekdays || '',
          hours_saturday: settings.hours_saturday || '',
          hours_sunday: settings.hours_sunday || ''
        });
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(formData);
      refreshSettings();
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="h-64 skeleton rounded-xl" />
        <div className="h-48 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="heading-1 mb-1">Settings</h1>
        <p className="body-small text-[var(--text-secondary)]">Manage your business information</p>
      </div>

      {/* Business Info */}
      <div className="bg-white rounded-xl p-6 space-y-5">
        <h2 className="heading-3 flex items-center gap-2">
          <MapPin size={18} />
          Business Information
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block body-small font-medium mb-1">Business Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-name-input"
            />
          </div>
          <div>
            <label className="block body-small font-medium mb-1">Tagline</label>
            <input
              type="text"
              name="tagline"
              value={formData.tagline}
              onChange={handleInputChange}
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-tagline-input"
            />
          </div>
        </div>

        <div>
          <label className="block body-small font-medium mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none resize-none"
            data-testid="settings-description-input"
          />
        </div>

        <div>
          <label className="block body-small font-medium mb-1">Address</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
            data-testid="settings-address-input"
          />
        </div>

        <div>
          <label className="block body-small font-medium mb-1">Location (City, Province)</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
            data-testid="settings-location-input"
          />
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-white rounded-xl p-6 space-y-5">
        <h2 className="heading-3 flex items-center gap-2">
          <Phone size={18} />
          Contact Information
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block body-small font-medium mb-1">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-phone-input"
            />
          </div>
          <div>
            <label className="block body-small font-medium mb-1">WhatsApp Number</label>
            <input
              type="tel"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleInputChange}
              placeholder="+15195550123"
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-whatsapp-input"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block body-small font-medium mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-email-input"
            />
          </div>
          <div>
            <label className="block body-small font-medium mb-1">Instagram Handle</label>
            <input
              type="text"
              name="instagram"
              value={formData.instagram}
              onChange={handleInputChange}
              placeholder="@homeglownails"
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-instagram-input"
            />
          </div>
        </div>

        <div>
          <label className="block body-small font-medium mb-1">Instagram URL</label>
          <input
            type="url"
            name="instagram_url"
            value={formData.instagram_url}
            onChange={handleInputChange}
            className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
            data-testid="settings-instagram-url-input"
          />
        </div>
      </div>

      {/* Business Hours */}
      <div className="bg-white rounded-xl p-6 space-y-5">
        <h2 className="heading-3 flex items-center gap-2">
          <Clock size={18} />
          Business Hours
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block body-small font-medium mb-1">Monday - Friday</label>
            <input
              type="text"
              name="hours_weekdays"
              value={formData.hours_weekdays}
              onChange={handleInputChange}
              placeholder="9:00 AM - 7:00 PM"
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-hours-weekdays-input"
            />
          </div>
          <div>
            <label className="block body-small font-medium mb-1">Saturday</label>
            <input
              type="text"
              name="hours_saturday"
              value={formData.hours_saturday}
              onChange={handleInputChange}
              placeholder="10:00 AM - 6:00 PM"
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-hours-saturday-input"
            />
          </div>
          <div>
            <label className="block body-small font-medium mb-1">Sunday</label>
            <input
              type="text"
              name="hours_sunday"
              value={formData.hours_sunday}
              onChange={handleInputChange}
              placeholder="By Appointment Only"
              className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              data-testid="settings-hours-sunday-input"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="settings-save-btn"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
